import { Dispatch, SetStateAction } from 'react'

import dayjs from 'dayjs'
import useSWR from 'swr'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  CompleteDistributionResponse,
  CompleteDistributionVars,
  DistributionDailySeriesResponse,
  DistributionDailySeriesVars,
  DistributionStatsResponse,
  DistributionStatsVars,
  GetDistributionResponse,
  GetDistributionVars,
  ListDistributionsResponse,
  ListDistributionsVars,
  RecommendationDistributionApiPath
} from '@/common/api/params/recommendationDistribution'
import { ApiCallBack } from '@/common/api/props'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import {
  RecommendationDistribution,
  RecommendationDistributionStatus
} from '@/common/model/recommend/recommendationDistribution'
import { WidgetContentType } from '@/common/model/recommend/widget'

export const useDistributionListApi = (vars: ListDistributionsVars) => {
  return useSWR<ListDistributionsResponse, Response, [string, ListDistributionsVars]>([
    RecommendationDistributionApiPath.LIST_DISTRIBUTIONS,
    vars
  ])
}

export const useDistributionGetApi = ({
  siteId,
  distributionId
}: {
  siteId: number
  distributionId: number
}) => {
  return useSWR<GetDistributionResponse, Response, [string, GetDistributionVars]>([
    RecommendationDistributionApiPath.GET_DISTRIBUTION,
    { site_id: siteId, distribution_id: distributionId }
  ])
}

export const useDistributionStatsApi = (vars: DistributionStatsVars | null) => {
  return useSWR<DistributionStatsResponse, Response, [string, DistributionStatsVars] | null>(
    vars ? [RecommendationDistributionApiPath.DISTRIBUTION_STATS, vars] : null
  )
}

export const useDistributionDailySeriesApi = (vars: DistributionDailySeriesVars | null) => {
  return useSWR<
    DistributionDailySeriesResponse,
    Response,
    [string, DistributionDailySeriesVars] | null
  >(vars ? [RecommendationDistributionApiPath.DISTRIBUTION_DAILY_SERIES, vars] : null)
}

// A/Bテストを確定する。バックエンドで勝者を確定し、ステータスは配信中(running)へ。
export const useCompleteDistribution = (callback?: ApiCallBack<CompleteDistributionResponse>) => {
  return useCallMutation<CompleteDistributionResponse, CompleteDistributionVars>(
    RecommendationDistributionApiPath.COMPLETE_DISTRIBUTION,
    {
      onSuccess: (data) => callback?.onSuccess?.(data)
    },
    apiPostFetcher
  )
}

// --- 一覧の検索条件（取得済みデータにクライアント側で適用する） ---
export interface DistributionSearchCondition extends SearchConditionBase {
  keyword: string | null
  contentType: WidgetContentType | null
  status: RecommendationDistributionStatus | null
  abEnabled: boolean | null
  distributesFrom: string | null
  distributesTo: string | null
}

const distributionSearchConditionParam: SearchConditionParam[] = [
  { name: 'keyword', type: 'string' },
  { name: 'contentType', type: 'string' },
  { name: 'status', type: 'string' },
  { name: 'abEnabled', type: 'boolean' },
  { name: 'distributesFrom', type: 'string' },
  { name: 'distributesTo', type: 'string' }
]

export const DISTRIBUTION_SEARCH_CONDITION_CLEAR: DistributionSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  keyword: null,
  contentType: null,
  status: null,
  abEnabled: null,
  distributesFrom: null,
  distributesTo: null
}

export const useDistributionSearchCondition = (): [
  DistributionSearchCondition,
  Dispatch<SetStateAction<DistributionSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<DistributionSearchCondition>(distributionSearchConditionParam)
}

export const filterDistributions = (
  distributions: RecommendationDistribution[],
  condition: DistributionSearchCondition
): RecommendationDistribution[] => {
  const keyword = condition.keyword?.trim().toLowerCase() ?? ''
  return distributions.filter((d) => {
    if (keyword) {
      const haystack = `${d.id} ${d.code} ${d.name}`.toLowerCase()
      if (!haystack.includes(keyword)) return false
    }
    if (condition.contentType && (d.variants[0]?.content_type ?? null) !== condition.contentType) {
      return false
    }
    if (condition.status && d.status !== condition.status) return false
    if (condition.abEnabled != null && d.ab_enabled !== condition.abEnabled) return false
    if (condition.distributesFrom) {
      if (!d.distributes_at) return false
      if (dayjs(d.distributes_at).isBefore(dayjs(condition.distributesFrom), 'day')) return false
    }
    if (condition.distributesTo) {
      if (!d.distributes_at) return false
      if (dayjs(d.distributes_at).isAfter(dayjs(condition.distributesTo), 'day')) return false
    }
    return true
  })
}
